import { BookingCalendarEventDetailInternalResponse } from 'api';
import { TimeSlot } from 'components/TimeSlotPicker';
import AvailableTimeSlots from './components/AvailableTimeSlots';
import { SuggestedTimeSlots } from './components/SuggestedTimeSlots';
import { NextButton } from 'components/NextButton';
import { useTranslation } from 'react-i18next';

import { useContext, useMemo } from 'react';
import { format } from 'utils/date';
import { ErrorMessage } from 'components/Inputs/components/ErrorMessage';
import { BookingFormContext } from 'pages/profile/context2';
import { BookingFormLayout } from 'pages/profile/components/Layout';
import { ProfileFooter } from 'pages/profile/components/Layout/components/ProfileFooter';
import clsx from 'clsx';

function isDuplicatedArray(arr: any[]) {
  if (arr.length !== new Set(arr).size) {
    return true;
  }

  return false;
}

export const SelectOrSuggestStep = () => {
  const { t } = useTranslation();
  const { advisorName, state, availableSlots, setState, setStep } = useContext(BookingFormContext);
  const onSuggestedTimeClick = () =>
    setState((draft) => {
      draft.workflow = draft.workflow === 'select-time' ? 'suggest-timespans' : 'select-time';
    });
  const lessThanTwoSuggestedSlotSelected = useMemo(
    () => state.suggestedSlots.filter((s) => !!s.startDate).length < 2,
    [state.suggestedSlots]
  );
  const hasDuplicatedSlots = useMemo(() => {
    return isDuplicatedArray(
      state.suggestedSlots.filter((s) => s.startDate).map((s) => format(s.startDate!, 'yyyy-mm-dd'))
    );
  }, [state.suggestedSlots]);

  const onGoNext = () => {
    setStep('enter-goal-and-purpose');
  };

  const isNextButtonDisabled = () => {
    // If users open Suggestion Aree (Interest Requests)
    if (state.workflow === 'suggest-timespans') {
      return lessThanTwoSuggestedSlotSelected || hasDuplicatedSlots;
    }

    return !state.selectedSlot;
  };

  const onSelectedSlotChange = (item: BookingCalendarEventDetailInternalResponse) => {
    setState((draft) => {
      draft.selectedSlot = item;
    });
  };

  const onSuggestedSlotsChange = (values: TimeSlot[]) => {
    setState((draft) => {
      draft.suggestedSlots = values;
    });
  };

  return (
    <>
      <BookingFormLayout
        footer={
          <ProfileFooter>
            <div
              className={clsx('flex items-center', {
                'justify-between': availableSlots.length > 0,
                'justify-end': availableSlots.length === 0,
              })}
            >
              {state.workflow === 'select-time' && (
                <div
                  className="flex items-center mt-4 mb-4 underline cursor-pointer font-openSans text-green text-base"
                  onClick={onSuggestedTimeClick}
                >
                  {t('REACT_BOOKING_FRONTEND.booking2.suggestTimeLink')}
                </div>
              )}

              {state.workflow === 'suggest-timespans' && availableSlots.length > 0 && (
                <div
                  className="flex items-center mt-4 mb-4 underline cursor-pointer font-openSans text-green text-base"
                  onClick={onSuggestedTimeClick}
                >
                  {t('REACT_BOOKING_FRONTEND.booking2.selectTimeLink', { advisorName })}
                </div>
              )}

              <NextButton onClick={onGoNext} disabled={isNextButtonDisabled()}>
                {t('REACT_BOOKING_FRONTEND.common.nextButton')}
              </NextButton>
            </div>

            {hasDuplicatedSlots && state.workflow === 'suggest-timespans' && (
              <div className="text-right">
                <ErrorMessage>{t('REACT_BOOKING_FRONTEND.suggestedSlots.duplicatedSlots')}</ErrorMessage>
              </div>
            )}
          </ProfileFooter>
        }
      >
        {state.workflow === 'select-time' && (
          <AvailableTimeSlots
            timeSlots={availableSlots}
            selectedTime={state.selectedSlot}
            onSelect={onSelectedSlotChange}
            disabled={false}
          />
        )}
        {state.workflow === 'suggest-timespans' && (
          <SuggestedTimeSlots
            timeslots={state.suggestedSlots}
            onChange={onSuggestedSlotsChange}
            advisorName={advisorName}
          />
        )}
      </BookingFormLayout>
    </>
  );
};
